import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, TextField, Typography } from '@mui/material';
import { AlertMessage } from '../../../components/AlertMessage';
import { ModalDialog } from '../../../components/ModalDialog';
import {
  StyledInputLabel,
  StyledSectionHeading,
} from '../../../components/inputs/InputLabel';
import { RichTextEditorField } from '../../../components/inputs/RichTextEditorField';
import { RichTextContent } from '../../../components/RichTextContent';
import type {
  Announcement,
  AnnouncementInput,
} from '../../../hooks/api/useAnnouncements';
import { ErrorObject } from '../../../types/errorhandling.types';
import { toFieldErrors, translateFieldError } from '../../../utils/apiErrors';

export type AnnouncementAction = 'saveDraft' | 'announce' | 'save' | 'retire';

const TITLE_MAX_LENGTH = 150;
const BODY_MAX_LENGTH = 20000;
const FORM_FIELDS = ['title', 'body_html'];
// API errors meaning someone else changed the announcement since the list was loaded.
const STALE_ERRORS = [
  'not_found',
  'announcement_changed',
  'announcement_retired',
  'invalid_transition',
];

/** Text content of the editor's HTML; trim() also drops the U+00A0 of `&nbsp;`. */
const textOf = (html: string) =>
  new DOMParser().parseFromString(html, 'text/html').body.textContent ?? '';

type AnnouncementFormDialogProps = {
  open: boolean;
  onClose: () => void;
  /** Undefined when creating a new announcement. */
  announcement?: Announcement;
  /** The announcement live right now, if any; announcing another one replaces it. */
  liveAnnouncement?: Announcement;
  /** Rejects with the API error body when the save failed. */
  onSubmit: (
    action: AnnouncementAction,
    data: AnnouncementInput
  ) => Promise<void>;
};

export const AnnouncementFormDialog = ({
  open,
  onClose,
  announcement,
  liveAnnouncement,
  onSubmit,
}: AnnouncementFormDialogProps) => {
  const { t } = useTranslation();
  const status = announcement?.status;
  const isRetired = status === 'retired';
  const isPublished = status === 'published';

  const [title, setTitle] = useState(announcement?.title ?? '');
  const [bodyHtml, setBodyHtml] = useState(announcement?.body_html ?? '');
  const [errors, setErrors] = useState<ErrorObject | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [pending, setPending] = useState<AnnouncementAction | null>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const replaced =
    !isPublished && !isRetired && liveAnnouncement?.id !== announcement?.id
      ? liveAnnouncement
      : undefined;
  const fieldError = (field: string) => {
    const message = errors?.[field]?.[0];
    return message ? translateFieldError(t, message) : undefined;
  };

  const fieldLabels: Record<string, string> = {
    title: t('announcements.dialog.titleLabel') as string,
    body_html: t('announcements.dialog.bodyLabel') as string,
  };

  /**
   * Shows field errors next to their fields, sums them up in the dialog's alert
   * (announced by screen readers) and moves focus to the first invalid field.
   */
  const reportFieldErrors = (fieldErrors: ErrorObject) => {
    setErrors(fieldErrors);
    const summary = Object.entries(fieldErrors).flatMap(([field, messages]) =>
      messages.map(
        (message) =>
          `${fieldLabels[field] ?? field}: ${translateFieldError(t, message)}`
      )
    );
    setError(`${t('announcements.dialog.fixErrors')} ${summary.join('. ')}.`);
    requestAnimationFrame(() => {
      if (fieldErrors.title) titleRef.current?.focus();
      else if (fieldErrors.body_html)
        bodyRef.current?.querySelector<HTMLElement>('.ProseMirror')?.focus();
    });
  };

  // Client-side check of what the server would reject as empty, so the buttons can
  // stay enabled (and in the tab order) and explain what's missing on press.
  const missingFields = (): ErrorObject => {
    const missing: ErrorObject = {};
    if (!title.trim()) missing.title = ['must be filled'];
    if (!textOf(bodyHtml).trim()) missing.body_html = ['must be filled'];
    return missing;
  };

  const submit = async (action: AnnouncementAction) => {
    if (pending) return;
    setErrors(undefined);
    setError(undefined);
    // Retiring sends no content, so it doesn't need a valid form.
    if (action !== 'retire') {
      const missing = missingFields();
      if (Object.keys(missing).length) {
        reportFieldErrors(missing);
        return;
      }
    }
    setPending(action);
    try {
      await onSubmit(
        action,
        action === 'retire'
          ? { status: 'retired' }
          : { title: title.trim(), body_html: bodyHtml }
      );
    } catch (err: unknown) {
      const fieldErrors = toFieldErrors(err);
      const apiError = (err as { error?: unknown } | null)?.error;
      if (fieldErrors) {
        // Errors on fields the form has no input for still show in the summary.
        const placed = Object.keys(fieldErrors).some((field) =>
          FORM_FIELDS.includes(field)
        );
        reportFieldErrors(fieldErrors);
        if (!placed) setErrors(undefined);
      } else if (
        typeof apiError === 'string' &&
        STALE_ERRORS.includes(apiError)
      ) {
        setError(t('announcements.dialog.staleError') as string);
      } else {
        setError(t('announcements.dialog.saveError') as string);
      }
    } finally {
      setPending(null);
    }
  };

  const titleError = fieldError('title');
  const bodyError = fieldError('body_html');

  const actionButton = (
    action: AnnouncementAction,
    variant: 'primary' | 'default' | 'secondary',
    label: string
  ) => (
    <Button
      variant={variant}
      onClick={() => submit(action)}
      // aria-disabled rather than disabled: the buttons stay focusable while a save runs
      aria-disabled={!!pending || undefined}
      aria-busy={pending === action || undefined}
    >
      {pending === action ? t('announcements.dialog.saving') : label}
    </Button>
  );

  const dialogTitle = (
    !announcement
      ? t('announcements.dialog.newTitle')
      : isRetired
        ? t('announcements.dialog.viewTitle')
        : t('announcements.dialog.editTitle')
  ) as string;

  return (
    <ModalDialog
      open={open}
      // Not while a save runs: its result belongs to this dialog, not a later one.
      onClose={() => {
        if (!pending) onClose();
      }}
      title={dialogTitle}
      subtitle={
        (isRetired
          ? t('announcements.dialog.retiredNote')
          : t('announcements.dialog.subtitle')) as string
      }
      error={error}
      actions={
        isRetired ? (
          <Button variant="default" onClick={onClose}>
            {t('announcements.dialog.close')}
          </Button>
        ) : isPublished ? (
          <>
            {actionButton(
              'retire',
              'secondary',
              t('announcements.dialog.retire')
            )}
            {actionButton(
              'save',
              'primary',
              t('announcements.dialog.saveChanges')
            )}
          </>
        ) : (
          <>
            {actionButton(
              'saveDraft',
              'default',
              t('announcements.dialog.saveDraft')
            )}
            {actionButton(
              'announce',
              'primary',
              t('announcements.dialog.announce')
            )}
          </>
        )
      }
    >
      {isRetired && announcement ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Box>
            <StyledSectionHeading>
              {t('announcements.dialog.titleLabel')}
            </StyledSectionHeading>
            <Typography lang="en">{announcement.title}</Typography>
          </Box>
          <Box>
            <StyledSectionHeading>
              {t('announcements.dialog.bodyLabel')}
            </StyledSectionHeading>
            <RichTextContent html={announcement.body_html} lang="en" />
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Box>
            <StyledInputLabel htmlFor="announcement-title">
              {t('announcements.dialog.titleLabel')}{' '}
              <span aria-hidden="true">*</span>
            </StyledInputLabel>
            <TextField
              fullWidth
              required
              id="announcement-title"
              inputRef={titleRef}
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setErrors(undefined);
              }}
              error={!!titleError}
              helperText={titleError}
              inputProps={{ maxLength: TITLE_MAX_LENGTH, lang: 'en' }}
            />
          </Box>
          <Box>
            <StyledSectionHeading id="announcement-body-label">
              {t('announcements.dialog.bodyLabel')}{' '}
              <span aria-hidden="true">*</span>
            </StyledSectionHeading>
            <Box ref={bodyRef}>
              <RichTextEditorField
                id="announcement-body"
                lang="en"
                value={bodyHtml}
                onChange={(html) => {
                  setBodyHtml(html);
                  setErrors(undefined);
                }}
                maxLength={BODY_MAX_LENGTH}
                error={!!bodyError}
                aria-labelledby="announcement-body-label"
                aria-label={t('announcements.dialog.bodyLabel') as string}
                aria-required
                aria-describedby={
                  bodyError ? 'announcement-body-error' : undefined
                }
                // Same sizing as the agreement description (LocalizedRichTextField).
                sx={{ mt: 4, '& .tiptap': { minHeight: '84px' } }}
              />
            </Box>
            {bodyError && (
              <Typography
                id="announcement-body-error"
                variant="caption"
                sx={{ color: 'var(--color-dark-grey-red)', mt: 4 }}
              >
                {bodyError}
              </Typography>
            )}
          </Box>
          {replaced && (
            <AlertMessage severity="warning">
              {t('announcements.dialog.replaceWarning', {
                title: replaced.title,
              })}
            </AlertMessage>
          )}
        </Box>
      )}
    </ModalDialog>
  );
};
